import { Vector2 } from "three";
import numeric from "numeric";
/**
 * @class Scene
 */
export class Lsm {
    constructor() {
        // Globale Variablen zur vereinfachten Nutzung
        // Variable zur Bestimmung des Patchversatzes
        this.offsetConstant = 6;
        // Die Minimale Distanz zwischen zwei Kreuzkorrelations-Peaks
        this.minimumPixelDistance = 5;
        // Tiefster Kreuzkorrelationswert der noch akzeptiert wird
        this.NCCCutoff = 0.75;
        // Tiefster alpha-wert nach der Ausgleichung der noch akzeptiert wird
        this.alphaCutoff = 1e-6;
        // Das minimum des Vektors der affinen transformations-standardabweichungen
        this.minAffParsNorm = 0.15;
        // Das gleiche einfach fuer die Radiometrie
        this.minRadParsNorm = 1.5;
        // Iterations-Stop Kriterium
        this.omegaIterationStop = 1.01;
        // Armijo parameter.
        this.mue = 0.1;
        // Kuerzeste Schrittlaenge
        this.alphaMin = 1e-9;
        // Wenn 1, dann wird die radiometrie automatisch mit ausgeglichen, sobald die
        // geometrischen Parameter genuegend gut bestimmt sind. Sonst wird die Radiometrie
        // vom parameter 'radiometry' bestimmt.
        this.autoradio = 1;
        this.onprogress = null;
        /*
         * Speichert Bild in einen Array
         *
         * @img Bild das in einen Array umkovertiert werden soll
         * @return gibt den Array zurueck
         */
        this._storeGrayValues = function (img) {
            let canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0, img.width, img.height);
            let matrix = [];
            for (let y = 0; y < img.height; ++y) {
                matrix.push([]);
                for (let x = 0; x < img.width; ++x) {
                    let pixelData = canvas.getContext("2d").getImageData(x, y, 1, 1);
                    // Wir nehmen hier den gruenen Kanal.
                    // Falls mit LAB => L gematcht werden will, muss
                    // RGB entsprechend multipliziert werden um RGB->XYZ->LAB zu erhalten
                    matrix[y].push(pixelData.data[1]);
                }
            }
            return matrix;
        };
        /*
         * Generiert eine 3x3 Filter Matrize zur Gradientenberechnung
         *
         * @return gibt die beiden Matrizen als Objekt zurueck
         * VORSICHT: WIRD HIER NICHT VERWENDET DA ES SICH NICHT NUR UM
         * GRADIENTEN SONDERN AUCH NOCH UM EINE GLAETTUNG HANDELT. DIES
         * FUEHRT ZU SEHR LANGSAMER KONVERGENZ IN LSM. DESHALB DA LIEBER
         * DIE METHODE EINES 1D-SOBELS WAEHLEN
         */
        this._createFilterMatrix = function () {
            // SCHARR Convolution
            let filterMatrixX = [];
            let filterMatrixY = [];
            filterMatrixX.push([-3, 0, 3], [-10, 0, 10], [-3, 0, 3]);
            filterMatrixY.push([-3, -10, -3], [0, 0, 0], [3, 10, 3]);
            let returnedObject = {};
            returnedObject["filterX"] = filterMatrixX;
            returnedObject["filterY"] = filterMatrixY;
            return returnedObject;
        };
        /*
         * Berechnet das Gradientenbild
         *
         * @imagevector Das bild als array
         * @patchsize Groesse des bildes (um randprobleme anzugehen)
         * @return Gibt Gradientenbild in x- und y-Richtung zureuck
         */
        this._obtainGradient = function (imagevector, patchsize) {
            let gradientmatrixX = [];
            let gradientmatrixY = [];
            for (let i = 0; i < imagevector.length; ++i) {
                let vy = Math.floor(i / patchsize);
                let vx = Math.floor(i % patchsize);
                // Randbereich ausgelassen, was aber o.k. ist,
                // da das bild sowieso 2 pixel groesser ist in beiden richtungen
                if (vx != 0 && vx != patchsize - 1 && vy != 0 && vy != patchsize - 1) {
                    // Gradient in X
                    let GradX = (imagevector[i + 1] - imagevector[i - 1]) / 2;
                    gradientmatrixX.push(GradX);
                    // Gradient in Y
                    let GradY = (imagevector[i + patchsize] - imagevector[i - patchsize]) / 2;
                    gradientmatrixY.push(GradY);
                }
            }
            let returnedObject = {};
            returnedObject["gradientX"] = gradientmatrixX;
            returnedObject["gradientY"] = gradientmatrixY;
            return returnedObject;
        };
        /*
         * Interpoliert
         *
         * @imagematrix 2D bild
         * @pos xy position
         * @rAff Radiometric parameters (imagematrix bekommt dadurch gain + offset)
         * @interpolationFunction wahl zwischen bilinear & lanczos2 & lanczos 3 (lanczos2 = 2-lobed lanczos, lanczos3 = 3-lobed lanczos)
         *
         * @return gibt gewichtetes grayvalue zurueck
         */
        this._interpolate2D = function (imagematrix, localPosition, rAff, interpolateFunction) {
            if (interpolateFunction == "bilinear") {
                let Bi0 = imagematrix[Math.floor(localPosition[1])][Math.floor(localPosition[0])];
                let Bi1 = imagematrix[Math.floor(localPosition[1])][Math.floor(localPosition[0]) + 1];
                let Bi2 = imagematrix[Math.floor(localPosition[1]) + 1][Math.floor(localPosition[0])];
                let Bi3 = imagematrix[Math.floor(localPosition[1]) + 1][Math.floor(localPosition[0]) + 1];
                // Radiometric gain & shift anbringen
                Bi0 = rAff[0] * Bi0 + rAff[1];
                Bi1 = rAff[0] * Bi1 + rAff[1];
                Bi2 = rAff[0] * Bi2 + rAff[1];
                Bi3 = rAff[0] * Bi3 + rAff[1];
                // Gewichte fuer Bilineare Interpolation
                let W0 = (localPosition[0] - Math.floor(localPosition[0])) * (localPosition[1] - Math.floor(localPosition[1]));
                let W1 = (1 - localPosition[0] + Math.floor(localPosition[0])) *
                    (localPosition[1] - Math.floor(localPosition[1]));
                let W2 = (localPosition[0] - Math.floor(localPosition[0])) *
                    (1 - localPosition[1] + Math.floor(localPosition[1]));
                let W3 = (1 - localPosition[0] + Math.floor(localPosition[0])) *
                    (1 - localPosition[1] + Math.floor(localPosition[1]));
                var grayValue = W0 * Bi3 + W1 * Bi2 + W2 * Bi1 + W3 * Bi0;
                return grayValue;
            }
            else if (interpolateFunction == "lanczos2") {
                var xs = [];
                xs.push(Math.floor(localPosition[0]) - 1);
                xs.push(xs[0] + 1);
                xs.push(xs[0] + 2);
                xs.push(xs[0] + 3);
                var ys = [];
                ys.push(Math.floor(localPosition[1]) - 1);
                ys.push(ys[0] + 1);
                ys.push(ys[0] + 2);
                ys.push(ys[0] + 3);
                var a = [];
                for (var i = 0; i < 4; ++i) {
                    var x = Math.abs(localPosition[0] - xs[i]);
                    if (x >= 2)
                        a.push(0);
                    else if (x == 0)
                        a.push(1);
                    else if (x < 2 && x >= 0) {
                        var lanczoswinsinc = ((Math.sin(Math.PI * x) / (Math.PI * x)) * Math.sin((Math.PI * x) / 2)) / ((Math.PI * x) / 2);
                        a.push(lanczoswinsinc);
                    }
                    else
                        return 0;
                }
                var Ik = [];
                for (var k = 0; k < 4; ++k) {
                    var IntSum = 0;
                    for (var i = 0; i < 4; ++i) {
                        IntSum += a[i] * (rAff[0] * imagematrix[ys[k]][xs[i]] + rAff[1]);
                    }
                    Ik.push(IntSum);
                }
                var b = [];
                for (var i = 0; i < 4; ++i) {
                    var x = Math.abs(localPosition[1] - ys[i]);
                    if (x >= 2)
                        b.push(0);
                    else if (x == 0)
                        b.push(1);
                    else if (x < 2 && x >= 0) {
                        var lanczoswinsinc = ((Math.sin(Math.PI * x) / (Math.PI * x)) * Math.sin((Math.PI * x) / 2)) / ((Math.PI * x) / 2);
                        b.push(lanczoswinsinc);
                    }
                    else
                        return 0;
                }
                var D = [];
                var grayValue = 0;
                for (var k = 0; k < 4; ++k) {
                    grayValue += b[k] * Ik[k];
                }
                return grayValue;
            }
            else if (interpolateFunction == "lanczos3") {
                var xs = [];
                xs.push(Math.floor(localPosition[0]) - 2);
                xs.push(xs[0] + 1);
                xs.push(xs[0] + 2);
                xs.push(xs[0] + 3);
                xs.push(xs[0] + 4);
                xs.push(xs[0] + 5);
                var ys = [];
                ys.push(Math.floor(localPosition[1]) - 2);
                ys.push(ys[0] + 1);
                ys.push(ys[0] + 2);
                ys.push(ys[0] + 3);
                ys.push(ys[0] + 4);
                ys.push(ys[0] + 5);
                var a = [];
                for (var i = 0; i < 6; ++i) {
                    var x = Math.abs(localPosition[0] - xs[i]);
                    if (x >= 2)
                        a.push(0);
                    else if (x == 0)
                        a.push(1);
                    else if (x < 2 && x >= 0) {
                        var lanczoswinsinc = ((Math.sin(Math.PI * x) / (Math.PI * x)) * Math.sin((Math.PI * x) / 3)) / ((Math.PI * x) / 3);
                        a.push(lanczoswinsinc);
                    }
                    else
                        return 0;
                }
                var Ik = [];
                for (var k = 0; k < 6; ++k) {
                    var IntSum = 0;
                    for (var i = 0; i < 6; ++i) {
                        IntSum += a[i] * (rAff[0] * imagematrix[ys[k]][xs[i]] + rAff[1]);
                    }
                    Ik.push(IntSum);
                }
                var b = [];
                for (var i = 0; i < 6; ++i) {
                    var x = Math.abs(localPosition[1] - ys[i]);
                    if (x >= 2)
                        b.push(0);
                    else if (x == 0)
                        b.push(1);
                    else if (x < 2 && x >= 0) {
                        var lanczoswinsinc = ((Math.sin(Math.PI * x) / (Math.PI * x)) * Math.sin((Math.PI * x) / 3)) / ((Math.PI * x) / 3);
                        b.push(lanczoswinsinc);
                    }
                    else
                        return 0;
                }
                var D = [];
                var grayValue = 0;
                for (var k = 0; k < 6; ++k) {
                    grayValue += b[k] * Ik[k];
                }
                return grayValue;
            }
        };
        /*
         * Fuellt A-Matrize
         *
         * @patch Search patch fuer radiometrie
         * @GradientsX Gradienten in X Richtung
         * @GradientsY Gradienten in Y Richtung
         * @patchsize groesse des zu generierenden Patches
         * @radiometry Flag ob radiometrie ausgeglichen werden soll oder nicht (addiert zwei zusaetzliche spalten,
         * was nacher bei den Verbesserungen noch addiert werden muss (zweimal ne 0, falls radio nicht ausgeglichen)
         *
         * @return gibt A-matrize zurueck als array
         */
        this._fillAMatrix = function (patch, GradientsX, GradientsY, patchsize, radiometry) {
            let A = [];
            let aIterator = 0;
            for (let y = -Math.floor(patchsize / 2); y <= Math.floor(patchsize / 2); ++y) {
                for (let x = -Math.floor(patchsize / 2); x <= Math.floor(patchsize / 2); ++x) {
                    // Ableitungen
                    let vDUda = [x, 0];
                    let vDUdb = [y, 0];
                    let vDUdc = [0, x];
                    let vDUdd = [0, y];
                    let vDUde = [1, 0];
                    let vDUdf = [0, 1];
                    let DgUda = GradientsX[aIterator] * vDUda[0] + GradientsY[aIterator] * vDUda[1];
                    let DgUdb = GradientsX[aIterator] * vDUdb[0] + GradientsY[aIterator] * vDUdb[1];
                    let DgUdc = GradientsX[aIterator] * vDUdc[0] + GradientsY[aIterator] * vDUdc[1];
                    let DgUdd = GradientsX[aIterator] * vDUdd[0] + GradientsY[aIterator] * vDUdd[1];
                    let DgUde = GradientsX[aIterator] * vDUde[0] + GradientsY[aIterator] * vDUde[1];
                    let DgUdf = GradientsX[aIterator] * vDUdf[0] + GradientsY[aIterator] * vDUdf[1];
                    A.push([]);
                    // Hier koennte an sich die Matrize variabel aufgebaut werden. D.h. insignifikante und/oder stark
                    // korrelierende Parameter koennten komplett deaktiviert werden (oder gewichtet). Ist momentan
                    // aber noch nicht fertig bzw. braucht zusatzaufwand
                    let DgUdr1 = patch[aIterator];
                    let DgUdr2 = 1;
                    A[aIterator].push(DgUda, DgUdb, DgUdc, DgUdd, DgUde, DgUdf, DgUdr1, DgUdr2);
                    aIterator++;
                }
            }
            return A;
        };
        this._Adjustment = function (fTemplate, fSearch, patchsize, searchposition) {
            //, tIntegralcontainer)
            /*
             * Fuehrt Ausgleichung aus
             *
             * @fTemplate canvas links
             * @fSearch canvas rechts
             * @patchsize groesse der patches
             * @tintegralcontainer container fuer template integral
             *
             * @return gibt loesungsvektor zurück
             */
            // Gedaempfte buendel ausgleichung via gauss-newton und armijo line search
            // Gemaess Niclas Boerlin:
            /*
            @inproceedings{Borlin397996,
            author = {B{\"o}rlin, Niclas},
            booktitle = {Close-range imaging, long-range vision : proceedings of the Commission V symposium},
            institution = {Umeå University, Department of Computing Science},
            number = {5},
            pages = {7--11},
            publisher = {ISPRS},
            title = {Improving the robustness of least squares template matching with a line-search algorithm},
            series = {ISPRS - International Archives of the Photogrammetry, Remote Sensing and Spatial Information Sciences},
            number = {Vol 34 part 5},
            volume = {34},
            year = {2002}
            }
    
            sowie
    
            @Article(Borlin2013:Bundle,
            author =       {Niclas B{\"o}rlin and Pierre Grussenmeyer},
            title =        {Bundle Adjustment with and without Damping},
            journal =      {Photogrammetric Record},
            year =         2013,
            volume =       28,
            number =       144,
            pages =        {396-415},
            month =        dec,
            doi =          {10.1111/phor.12037}
            )
    
            */
            // Konvergenz-Flag
            let converged = 0;
            // Anzahl iterationen
            let iteration = 0;
            // Radiometrie-flag. Wird bei this.autoradio = 1 automatisch auf 1 gesetzt, sobald
            // die geometrischen Parameter von genuegender qualitaet sind. Empfohlen, auf 0 zu lassen
            let radiometry = 0;
            // Grusiger Hack um min. eine Iteration mit radiometrie zu erzwingen.
            let iterradioswitch = 0;
            // Position im Canvas
            let positionTemplate = [Math.floor(fTemplate.length / 2), Math.floor(fTemplate[0].length / 2)];
            let positionSearch = [searchposition[0], searchposition[1]];
            // Vektor mit allen 8 Parametern (6 affin, 2 radio)
            let affineMatrixTemplate = [1, 0, 0, 1, positionTemplate[1], positionTemplate[0], 1, 0];
            let affineMatrixSearch = [1, 0, 0, 1, positionSearch[1], positionSearch[0], 1, 0];
            // Template patch generieren
            let templateorig = this._generatePatch(fTemplate, affineMatrixTemplate, patchsize);
            let template = this._Wallisfilter(templateorig);
            // Abbrechen falls template am Rand (sehr unwahrscheinlich - falls es passiert, ist
            // wohl ein Wurm drin
            if (template == 0)
                return 0;
            // Template ins Canvas zeichnen
            //this._drawPatchToCanvas("canvaspatch_l", template, patchsize);
            // Loesungsvektor setzen
            let x = affineMatrixSearch;
            let xold = x;
            let epsilon = 1e-1;
            let r = 1e10;
            let omega0 = 0;
            let omega = 0;
            let numpar = 8;
            let NCC = 0;
            let A = [];
            let ATA = [];
            let ATR = [];
            // Stochastische Gewichtung der Unbekannten (nach DGAP...experimentell)
            let StochasticWeights = numeric.diag([1, 1, 1, 1, 1, 1, 1, 1]);
            while (converged == 0) {
                if (this.onprogress) {
                    this.onprogress();
                }
                // Search patch generieren (+ 2 fuer gradienten - wird nacher nicht mehr benoetigt)
                let searchGradsBorder = this._generatePatch(fSearch, x, patchsize + 2);
                if (searchGradsBorder == 0)
                    return 0;
                // Gradienten fuer Search
                let gradienten = this._obtainGradient(searchGradsBorder, patchsize + 2);
                let searchGradX = gradienten.gradientX;
                let searchGradY = gradienten.gradientY;
                // Search generieren - bei rand-ueberlauf, abbrechen
                let searchorig = this._generatePatch(fSearch, x, patchsize);
                let search = this._Wallisfilter(searchorig);
                if (search == 0)
                    return 0;
                // Initialposition von search malen (obsolet, da nun mehrere
                // Loesungen existieren koennen und entsprechend dann auch
                // Falsche approximationen daher kommen
                if (iteration == 0)
                    if (iteration == 0) {
                        //this._drawPatchToCanvas("canvaspatch_r", search, patchsize);
                        // Residuen vektor (nur am Anfang - nacher wirds durch linesearch geregelt
                        r = numeric.sub(template, search);
                        NCC = this._NormCrossCor(template, search); //, tIntegralcontainer, sIntegralContainer);
                    }
                // Correlation check
                // A-Matrize fuellen
                A = this._fillAMatrix(search, searchGradX, searchGradY, patchsize, radiometry);
                // ATA
                ATA = numeric.dot(numeric.transpose(A), A);
                // Gewichte fuer radiometrie
                if (radiometry == 0) {
                    StochasticWeights[6][6] = 1e32;
                    StochasticWeights[7][7] = 1e32;
                }
                else {
                    StochasticWeights[6][6] = 1;
                    StochasticWeights[7][7] = 1;
                }
                // Gewichte anbringen
                ATA = numeric.add(ATA, StochasticWeights);
                // ATR
                ATR = numeric.dot(numeric.transpose(A), r);
                // l um ATR zu gewichten (ist glaub so falsch weil beobachtet & approx gleich sin)
                /*
                var l = numeric.sub(x, xold);
                var pl = [];
                for (var i = 0; i < StochasticWeights.length; ++i)
                {
                pl.push(StochasticWeights[i][i] * l[i]);
                }
                ATR = numeric.add(ATR, pl);*/
                // Loesen lineares gleichungssystem
                // An sich liesse sich ATA und ATR vermeiden wenn man eine
                // SVD rechnet. Allerdings weiss ich dann nicht, ob
                // Gewichtungen dann noch moeglich sind. Probleme kann
                // es geben, wenn A-Matrize schlecht konditioniert sind
                let p = numeric.solve(ATA, ATR);
                // Ap = ax
                let Ap = numeric.dot(A, p);
                // fp0
                let fp0 = numeric.dot(Ap, r);
                // Verbesserungen
                let v = numeric.sub(Ap, r);
                // Abbruchskriterium ueber 0.5 * vTv
                omega0 = omega;
                omega = 0.5 * numeric.dot(v, v);
                // Linesearch
                // Da ist u.U. noch ein Fehler drin. Sobald man sich der Loesung naehert
                // wird ~2-3 iterationen vor der Loesung das Alpha minimal. Eigentlich waere das
                // ein Abbruchkriterium - aber laesst man es mit voller alpha-laenge laufen
                // konvergiert es 1-2 iterationen spaeter. Ich nehme an, dass
                // es sich um Rundungsfehler handelt da die Daten groesstenteils rausch-befreit sind
                // Entsprechend wurde die ungleichung f < f0 + this.mue * alpha * fp0 umgeschrieben in
                // f < 1 + f0 + this.mue * alpha * fp0. Trotzdem sollte da noch genauer kontrolliert
                // werden obs stimmt, da es nun praktisch keine alpha < 1e-9 Faelle mehr gibt
                let linesearchres = this._Linesearch(x, p, r, fp0, this.alphaMin, this.mue, template, search, fSearch, patchsize); //, tIntegralcontainer);
                // Falls Bild an rand kommt
                if (linesearchres == 0)
                    return 0;
                // Zurueck-gegebene Parameter von linesearch
                var alpha = linesearchres.alphaLength;
                let xNew = linesearchres.xNew;
                let rNew = linesearchres.rNew;
                NCC = linesearchres.NCC;
                var searchUpdate = linesearchres.searchUpdate;
                // Updates
                xold = x;
                x = xNew;
                r = rNew;
                // Boerlin-abbruch Kriterium. Viel zu strikt oder zu schwach. Ueberpruefen
                let left = numeric.norm2(Ap);
                let right = epsilon * numeric.norm2(r);
                // Sigma0
                let sigma0 = Math.sqrt(omega / (Math.pow(patchsize, 2) - numpar));
                // Abschliessend waere hier die Ueberpruefung der Korrelationen
                // um allfaellige Parameter die stark korrelieren zu entfernen
                // oder abzuschwaechen, wenn sie schwach korrelieren. Es this.muesste
                // zuerst auch noch ein Signifikanztest durchgefuehrt werden um
                // nicht-signifikante Parameter auzuschalten.
                // Da dies zeit braucht, wird momentan nur der Radiometrie-Parameter
                // stochastisch gewichtet, je nachdem ob er eingeschaltet
                // ist oder nicht (isch no witzig, odr?)
                let ATAINV = numeric.inv(ATA);
                var parstddev = [];
                for (var i = 0; i < numpar; ++i) {
                    parstddev.push(Math.sqrt(sigma0 * ATAINV[i][i]));
                }
                let Correlation = [];
                let MaxPos = [];
                for (var i = 0; i < numpar; ++i) {
                    Correlation.push([]);
                    let ix = ATAINV[i][i];
                    for (var j = 0; j < numpar; ++j) {
                        let iy = ATAINV[j][j];
                        Correlation[i].push(ATAINV[i][j] / Math.sqrt(ix * iy));
                    }
                }
                let UpperPar = numpar - 1;
                for (var i = 0; i < numpar - 1; ++i) {
                    for (var j = 1; j < UpperPar; ++j) {
                        if (Correlation[i][j] > 0.99 && i != j) {
                            if (Correlation[i][j] > Correlation[j][i])
                                MaxPos.push(i, j, 1e20, Correlation[i][j]);
                            else
                                MaxPos.push(j, i, 1e20, Correlation[j][i]);
                        }
                        else if (Correlation[i][j] > 0.9 && i != j) {
                            if (Correlation[i][j] > Correlation[j][i])
                                MaxPos.push(i, j, 10 / Correlation[i][j], Correlation[i][j]);
                            else
                                MaxPos.push(j, i, 10 / Correlation[j][i], Correlation[j][i]);
                        }
                        else if (Correlation[i][j] > 0.7 && i != j) {
                            if (Correlation[i][j] > Correlation[j][i])
                                MaxPos.push(i, j, 5 / Correlation[i][j], Correlation[i][j]);
                            else
                                MaxPos.push(j, i, 5 / Correlation[j][i], Correlation[j][i]);
                        }
                        else if (Correlation[i][j] > 0.5 && i != j) {
                            if (Correlation[i][j] > Correlation[j][i])
                                MaxPos.push(i, j, 2 / Correlation[i][j], Correlation[i][j]);
                            else
                                MaxPos.push(j, i, 2 / Correlation[j][i], Correlation[j][i]);
                        }
                        else if (Correlation[i][j] > 0.3 && i != j) {
                            if (Correlation[i][j] > Correlation[j][i])
                                MaxPos.push(i, j, 1 / Correlation[i][j], Correlation[i][j]);
                            else
                                MaxPos.push(j, i, 1 / Correlation[j][i], Correlation[j][i]);
                        }
                    }
                    --UpperPar;
                }
                // Iteration hoch-schrauben
                iteration++;
                if (iteration >= 50) {
                    // Abbruch falls zu viele Iterationen
                    converged = 2;
                }
                else if (alpha < this.alphaMin) {
                    // Abbruch falls alpha kleiner als this.alphaMin
                    converged = 2;
                }
                //else if (left <= right) // Boerlin-abbruch. Bricht ganze Haeuser ein bevor es mal klappt...
                //converged = 1;
                if (iteration > 0 &&
                    omega0 / omega < this.omegaIterationStop &&
                    omega0 / omega > 1 / this.omegaIterationStop) {
                    // Abbruch ueber verbesserungsvergleich
                    if (this.autoradio == 1) {
                        if (radiometry == 0)
                            iterradioswitch = iteration; // Falls automatisch radiometrie zugeschaltet, hier einschalten
                        radiometry = 1;
                        if (omega0 / omega < this.omegaIterationStop &&
                            omega0 / omega > 1 / this.omegaIterationStop &&
                            iteration > iterradioswitch) {
                            // Zweite ueberpruefung nach einschalten von radiometrie-ausgleich
                            // Signifikanztest
                            let Signifikanz = [];
                            for (var i = 0; i < numpar; ++i) {
                                let Change = numeric.sub(x, affineMatrixSearch);
                                Signifikanz.push(Math.abs(Change[i] / parstddev[i]));
                            }
                            var affstddev = numeric.getBlock(parstddev, [0, 0], [5, 0]);
                            var radiostddev = numeric.getBlock(parstddev, [6, 0], [7, 0]);
                            var normaffstddev = numeric.norm2(affstddev);
                            var normradstddev = numeric.norm2(radiostddev);
                            if (normaffstddev > this.minAffParsNorm || normradstddev > this.minRadParsNorm)
                                converged = 2;
                            else
                                converged = 1;
                        }
                    }
                    else if (omega0 / omega < this.omegaIterationStop && omega0 / omega > 1 / this.omegaIterationStop)
                        converged = 1;
                }
            }
            // Falls keine Konvergenz => null zurueck geben
            if (converged == 0 || converged == 2)
                return 0;
            // Ansonsten den ganzen Plunder hoch schicken
            else if (converged == 1) {
                let returnedObject = {};
                returnedObject["alphaLength"] = alpha;
                returnedObject["x"] = x;
                returnedObject["r"] = r;
                returnedObject["NCC"] = NCC;
                returnedObject["searchUpdate"] = searchUpdate;
                returnedObject["ParsStdDev"] = parstddev;
                returnedObject["AffStdDev"] = affstddev;
                returnedObject["RadStdDev"] = radiostddev;
                returnedObject["NormAffStdDev"] = normaffstddev;
                returnedObject["NormRadStdDev"] = normradstddev;
                return returnedObject;
            }
        };
        /*
         * Fuehrt Armijo-Linesearch aus
         *
         * @x momentaner loesungsvektor
         * @p verbesserungen
         * @r residuen
         * @fp0 A*p*r'
         * @this.alphaMin minimale Schrittlaenge fuer Gauss-Newton
         * @this.mue Armijo parameter
         * @template template patch
         * @search search patch
         * @fSearch Search canvas
         * @patchsize groesse der Patches
         * @tIntegralContainer Integralbilder template
         *
         * @return gibt verbesserung, residuen, ncc und schrittlaenge zurueck
         */
        this._Linesearch = function (x, p, r, fp0, alphaMin, mue, template, search, fSearch, patchsize //, tIntegralcontainer)
        ) {
            // 0.5 * vTpV
            let f0 = 0.5 * numeric.dot(r, r);
            // Start-alpha (schritt-laenge)
            let alpha = 1.0;
            // Solange iterieren bis alpha zu klein
            while (Math.abs(alpha) >= this.alphaMin) {
                // Neuer loesungsvektor
                let trialpoint = numeric.add(x, numeric.dot(alpha, p));
                // neues patch - abbrechen falls Bild ueber Rand gezeichnet
                let searchUpdateorig = this._generatePatch(fSearch, trialpoint, patchsize);
                var searchUpdate = this._Wallisfilter(searchUpdateorig);
                if (searchUpdate == 0)
                    return 0;
                // Neuer Residuenvektor
                let rLoc = numeric.sub(template, searchUpdate);
                // Neue NCC
                var NCC = this._NormCrossCor(template, searchUpdate); //, tIntegralcontainer, sIntegralcointainer);
                // neues 0.5 * vTpV
                let f = 0.5 * numeric.dot(rLoc, rLoc);
                // Armijo Kriterium, rechte Seite
                //var fArmijo = 0.1 + f0 + this.mue * alpha * fp0;
                let fArmijo = 0.1 + f0 + this.mue * alpha * fp0;
                // Falls neuer funktionswert (0.5 * vTpV) kleiner als Armijo-kriterium, Loesung akzeptieren...
                if (f < fArmijo) {
                    var returnedObject = {};
                    returnedObject["alphaLength"] = alpha;
                    returnedObject["Change"] = numeric.dot(alpha, p);
                    returnedObject["xNew"] = trialpoint;
                    returnedObject["rNew"] = rLoc;
                    returnedObject["NCC"] = NCC;
                    returnedObject["searchUpdate"] = searchUpdate;
                    return returnedObject;
                }
                // ...sonst loesung halbieren
                else {
                    alpha = alpha / 2;
                }
            }
            // Hier den Plunder zurueckgeben, auch wenn keine vernuenftige Loesung gefunden
            var returnedObject = {};
            returnedObject["alphaLength"] = 0;
            returnedObject["xNew"] = numeric.add(x, p);
            returnedObject["rNew"] = r;
            returnedObject["NCC"] = NCC;
            returnedObject["searchUpdate"] = searchUpdate;
            return returnedObject;
        };
        /*
         * Berechnet normalisierten Kreuzkorrelationskoeffizient (NCC)
         *
         * @template template patch
         * @search search patch
         * @tintegral template integral container (noch nicht drin)
         * @sintegral search integral container (noch nicht drin)
         *
         * @return gibt NCC zurueck
         */
        this._NormCrossCor = function (template, search //, tintegral, sintegral)
        ) {
            let imagesize = template.length;
            let patchsize = Math.sqrt(imagesize);
            let tMean = 0;
            let sMean = 0;
            let tStd = 0;
            let sStd = 0;
            // Mittelwerte berechnen
            for (var i = 0; i < imagesize; ++i) {
                tMean += template[i];
                sMean += search[i];
            }
            tMean /= imagesize;
            sMean /= imagesize;
            //var intmean = tintegral.Integral[y][x];
            for (var i = 0; i < imagesize; ++i) {
                tStd += Math.pow(template[i] - tMean, 2);
                sStd += Math.pow(search[i] - sMean, 2);
            }
            tStd = Math.sqrt(tStd / imagesize);
            sStd = Math.sqrt(sStd / imagesize);
            let NCC = 0;
            for (var i = 0; i < imagesize; ++i) {
                NCC += ((template[i] - tMean) * (search[i] - sMean)) / (tStd * sStd);
            }
            NCC *= 1 / imagesize;
            // NCC via Vektoren berechnen. Dauert wohl laenger als standard-fall weil:
            // Standard:
            // 1x durch patch fuer mean
            // 1x durch patch fuer stdv
            // 1x durch patch fuer NCC
            // Vektoren:
            // 1x durch patch fuer mean
            // 1x (bzw. 2x) durch Vektoren um mean abzuziehen
            // 1x (bzw. 2x) durch Vektoren um norm zu dividieren
            // 1x durch Vektor fuer inneres vektorprodukt
            // da 1x durch Vektor = 1x durch Patch, ergibt sich:
            // 3x durch patch fuer standard
            // min. 4x (oder wie hier 6x) durch patch fuer Vektoren
            // So oder so sollte es mit integralbildern gemacht werden
            /*
            var T = numeric.sub(template, tMean);
            var F = numeric.sub(search, sMean);
    
            var left = numeric.div(T, numeric.norm2(T));
            var right = numeric.div(F, numeric.norm2(F));
    
            var NCC = numeric.dot(left, right);*/
            return NCC;
        };
    }
    /**
     * @param left_dataurl
     * @param right_dataurl
     */
    runLsm(left_dataurl, right_dataurl) {
        // Bilder in den Canvas zeichnen (davon wird dann matrize generiert)
        let leftimg = new Image();
        let rightimg = new Image();
        leftimg.src = left_dataurl;
        rightimg.src = right_dataurl;
        if (this.onprogress) {
            window.setTimeout(() => {
                this.onprogress();
            }, 0);
        }
        // Patchgroesse definieren (evt im Dienst loesen als aufziehbares Rechteck?)
        let patchsize = 15;
        // Linkes & rechtes Bild in einen Array speichern
        let fTemplate = this._storeGrayValues(leftimg);
        let fSearch = this._storeGrayValues(rightimg);
        if (this.onprogress) {
            window.setTimeout(() => {
                this.onprogress();
            }, 0);
        }
        // Integralbilder generieren (um Wallisfilter anzuwenden)
        let integralTemplate = this._IntegralImage(fTemplate);
        let integralSearch = this._IntegralImage(fSearch);
        if (this.onprogress) {
            window.setTimeout(() => {
                this.onprogress();
            }, 0);
        }
        // Template patch generieren (position in der Mitte des Bildes und groesse von patchsize
        let positionTemplate = [Math.floor(fTemplate.length / 2), Math.floor(fTemplate[0].length / 2)];
        let position = [1, 0, 0, 1, positionTemplate[1], positionTemplate[0], 1, 0];
        let templateorig = this._generatePatch(fTemplate, position, patchsize);
        let template = this._Wallisfilter(templateorig);
        if (this.onprogress) {
            window.setTimeout(() => {
                this.onprogress();
            }, 0);
        }
        // Kreuzkorrelation entlang der Epipolarlinie (1 pixel momentan)
        let NCC = [];
        for (var i = this.offsetConstant; i < fSearch[0].length - (patchsize + this.offsetConstant); ++i) {
            var positionSearch = [Math.floor(fSearch.length / 2), i + Math.floor(patchsize / 2)];
            position = [1, 0, 0, 1, positionSearch[1], positionSearch[0], 1, 0];
            let searchorig = this._generatePatch(fSearch, position, patchsize);
            let search = this._Wallisfilter(searchorig);
            //var integralSearch = this._IntegralImage(fSearch);
            NCC.push(this._NormCrossCor(template, search)); //, integralSearch, integralTemplate));
            if (i % 20 == 0 && this.onprogress) {
                window.setTimeout(() => {
                    this.onprogress();
                }, 0);
            }
        }
        // Ueberprueft maximas und speichert diese sofern sie groesser als 0.5 sind
        // Sobald der Wert sinkt, wird 5 pixel abgewartet - sofern der Wert dann nicht
        // steigt, wird nach einem neuen Peak gesucht. Sonst wars nur ein Buckel zum maximum
        // Offset um wahre Pixelposition zu bestimmen
        let Maximas = [];
        let localmaximum = 0;
        let peakflag = 0;
        let peakpos = -1;
        let offset = this.offsetConstant + Math.floor(patchsize / 2);
        for (var i = 0; i < NCC.length; ++i) {
            if (NCC[i] > this.NCCCutoff && NCC[i] > localmaximum) {
                localmaximum = NCC[i];
                peakpos = i + offset;
                peakflag = this.minimumPixelDistance;
            }
            else if (peakflag > 0) {
                --peakflag;
                if (peakflag == 0) {
                    Maximas.push(positionSearch[0], peakpos);
                    peakpos = -1;
                    localmaximum = 0;
                }
            }
            if (this.onprogress) {
                window.setTimeout(() => {
                    this.onprogress();
                }, 0);
            }
        }
        // Loesungsraum
        let finalsolutions = [];
        // Alle Positionskandidaten durchs LSM jagen
        for (var i = 0; i < Maximas.length; i += 2) {
            position = [Maximas[i], Maximas[i + 1]];
            let solution = this._Adjustment(fTemplate, fSearch, patchsize, position); //, integralTemplate);
            // Falls Loesung gefunden, hier abspeichern
            if (solution != 0) {
                var Object = {};
                Object["alphaLength"] = solution.alphaLength;
                Object["x"] = solution.x;
                Object["r"] = solution.r;
                Object["NCC"] = solution.NCC;
                Object["searchUpdate"] = solution.searchUpdate;
                Object["ParsStdDev"] = solution.ParsStdDev;
                Object["NormAffStdDev"] = solution.NormAffStdDev;
                Object["NormRadStdDev"] = solution.NormRadStdDev;
                finalsolutions.push(Object);
            }
        }
        let maxNCC = this.NCCCutoff;
        let selectedSolution = -1;
        let localminAffParsNorm = this.minAffParsNorm;
        let localminRadParsNorm = this.minRadParsNorm;
        // Durch jede Loesung durch gehen und maximum NCC auswaehlen
        // unter beruecksichtigung dass Alpha nicht zu klein ist.
        for (var i = 0; i < finalsolutions.length; ++i) {
            if (finalsolutions[i].NCC > maxNCC &&
                finalsolutions[i].alphaLength > this.alphaCutoff &&
                finalsolutions[i].NormAffStdDev < localminAffParsNorm &&
                finalsolutions[i].NormRadStdDev < localminRadParsNorm) {
                selectedSolution = i;
                maxNCC = finalsolutions[i].NCC;
                localminAffParsNorm = finalsolutions[i].NormAffStdDev;
                localminRadParsNorm = finalsolutions[i].NormRadStdDev;
            }
        }
        // Loesung ins canvas malen
        if (selectedSolution > -1) {
            let result = new Vector2(finalsolutions[0].x[4], finalsolutions[0].x[5]);
            return result;
        }
        else {
            let colour = {};
            colour["red"] = 255;
            colour["green"] = 0;
            colour["blue"] = 0;
            finalsolutions.push(Object);
            return null;
        }
    }
    /*
     * Berechnet Wallisfilter (benoetigt Integralbild
     *
     * @imagematrix Bild als array
     * @return integral und integralsquared zurueck
     */
    _Wallisfilter(patch) {
        let wallispatch = [];
        let contrastconstant = 0.9;
        let targetmean = 127.5;
        let targetstdv = 90;
        let brightnessconstant = 1.0;
        let mean = 0;
        let std = 0;
        for (var i = 0; i < patch.length; ++i) {
            mean += patch[i];
        }
        mean = mean / patch.length;
        for (var i = 0; i < patch.length; ++i) {
            std += Math.pow(patch[i] - mean, 2);
        }
        std = Math.sqrt(std / patch.length);
        let conTarg = contrastconstant * targetstdv;
        let minusConTarg = (1 - contrastconstant) * targetstdv;
        let brighTarg = brightnessconstant * targetmean;
        let r1 = conTarg / (contrastconstant * std + minusConTarg);
        let r0 = brighTarg + (1 - brightnessconstant - r1) * mean;
        for (let g = 0; g < patch.length; ++g) {
            let gc = patch[g] * r1 + r0;
            if (gc > 255.0)
                wallispatch[g] = 255;
            else if (gc < 0.0)
                wallispatch[g] = 0;
            else
                wallispatch[g] = gc;
        }
        return wallispatch;
    }
    /*
     * Berechnet integralbild
     *
     * @imagematrix Bild als array
     * @return integral und integralsquared zurueck
     */
    _IntegralImage(imagematrix) {
        let Integral = [];
        let IntegralSq = [];
        for (let y = 0; y < imagematrix.length; ++y) {
            Integral.push([]);
            IntegralSq.push([]);
            for (let x = 0; x < imagematrix[0].length; ++x) {
                let Left = 0;
                let Top = 0;
                let TopLeft = 0;
                if (x > 0)
                    Left = Integral[y][x - 1];
                if (y > 0)
                    Top = Integral[y - 1][x];
                if (x > 0 && y > 0)
                    TopLeft = Integral[y - 1][x - 1];
                let IntegralVal = imagematrix[y][x] + Left + Top - TopLeft;
                Integral[y].push(IntegralVal);
                IntegralSq[y].push(Math.pow(IntegralVal, 2));
            }
        }
        let returnedObject = {};
        returnedObject["Integral"] = Integral;
        returnedObject["IntegralSq"] = IntegralSq;
        return returnedObject;
    }
    /*
     * Erstellt patch aus img, unter beruecksichtigung einer affinen trafo und interpoliert bilinear
     *
     * @imagematrix 2D bild
     * @affineTransformation Affine transformationsmatrize, als Vektor.
     * 0, 1 = erste Zeile, 2, 3 = zweite Zeile, 4, 5 = translation x,y, 6, 7 = radiometric gain & shift
     * @patchsize groesse des zu generierenden Patches
     *
     * @return gibt patch zurueck als array
     */
    _generatePatch(imagematrix, affineTransformation, patchsize) {
        let vectoriterator = 0;
        let patch = [];
        // Rotation, Scherungen, Massstaebe
        let mAff = [
            [affineTransformation[0], affineTransformation[1]],
            [affineTransformation[2], affineTransformation[3]],
        ];
        // Translationen
        let vAff = [[affineTransformation[4]], [affineTransformation[5]]];
        // Radiometrie
        let rAff = [affineTransformation[6], affineTransformation[7]];
        let outOfBounds = 0;
        for (let y = -Math.floor(patchsize / 2); y <= Math.floor(patchsize / 2); ++y) {
            // Falls waehrend der Interpolation ausserhalb des Bildes zugegriffen wird
            // muss abgebrochen werden. Alternative: Patch verkleinern (nein danke...)
            if (outOfBounds == 1)
                break;
            for (let x = -Math.floor(patchsize / 2); x <= Math.floor(patchsize / 2); ++x) {
                let vPos = [[x], [y]];
                // Vorsicht, localposition wie auch vPos sind [X,Y] und nicht umgekehrt
                let localPosition = numeric.add(numeric.dot(mAff, vPos), vAff);
                if (localPosition[1] < this.offsetConstant ||
                    localPosition[1] > imagematrix.length - this.offsetConstant ||
                    localPosition[0] < this.offsetConstant ||
                    localPosition[0] > imagematrix[0].length - this.offsetConstant) {
                    outOfBounds = 1;
                    break;
                }
                // Interpolieren (bilinear, lanczos2,3)
                let grayValue = this._interpolate2D(imagematrix, localPosition, rAff, "lanczos2");
                patch.push(grayValue);
            }
        }
        if (outOfBounds == 0)
            return patch;
        else
            return 0;
    }
}
//# sourceMappingURL=lsm.js.map